import { Button, Paper, Theme, Typography, withStyles } from '@material-ui/core';
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
	classes: any;
	children: ReactNode;
}

interface State {
	hasError: boolean;
	isChunkError: boolean;
}

const isChunkLoadError = (error: Error): boolean => {
	const message = error?.message || '';
	return (
		message.includes('Failed to fetch dynamically imported module') ||
		message.includes('Loading chunk') ||
		message.includes('ChunkLoadError') ||
		message.includes('is not a valid JavaScript MIME type') ||
		error.name === 'ChunkLoadError'
	);
};

class ChunkErrorBoundary extends Component<Props, State> {
	public state: State = {
		hasError: false,
		isChunkError: false
	};

	public static getDerivedStateFromError(error: Error): State {
		return {
			hasError: true,
			isChunkError: isChunkLoadError(error)
		};
	}

	public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error('ChunkErrorBoundary caught error:', error, errorInfo);
	}

	private handleRefresh = (): void => {
		window.location.reload();
	};

	public render(): ReactNode {
		const { classes, children } = this.props;
		const { hasError, isChunkError } = this.state;

		if (hasError && isChunkError) {
			return (
				<div className={classes.container}>
					<div className={classes.wrapper}>
						<Paper className={classes.root} elevation={1}>
							<Typography variant="h5" component="h3" gutterBottom>
								Update Available
							</Typography>
							<img
								src="https://assets.4giving.com/email/giving_guy_72_82_2x.png"
								width="72"
								height="82"
								alt="4giving Guy"
							/>
							<Typography component="p" gutterBottom>
								A new version is available. Please refresh the page to continue.
							</Typography>
							<Button variant="contained" color="primary" onClick={this.handleRefresh}>
								Refresh Page
							</Button>
						</Paper>
					</div>
				</div>
			);
		}

		if (hasError) {
			return (
				<div className={classes.container}>
					<div className={classes.wrapper}>
						<Paper className={classes.root} elevation={1}>
							<Typography variant="h5" component="h3" gutterBottom>
								Something Went Wrong
							</Typography>
							<img
								src="https://assets.4giving.com/email/giving_guy_72_82_2x.png"
								width="72"
								height="82"
								alt="4giving Guy"
							/>
							<Typography component="p" gutterBottom>
								We encountered an unexpected error. Please try refreshing the page.
							</Typography>
							<Button variant="contained" color="primary" onClick={this.handleRefresh}>
								Refresh Page
							</Button>
						</Paper>
					</div>
				</div>
			);
		}

		return children;
	}
}

const styles = (theme: Theme): any => ({
	container: {
		marginTop: 100
	},
	root: {
		...theme.mixins.gutters(),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		width: 300,
		textAlign: 'center'
	},
	wrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%'
	}
});

export default withStyles(styles)(ChunkErrorBoundary);
